import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

const STATUS_LABELS = {
  pending: { label: 'Payment Pending', color: 'warning' },
  paid: { label: 'Confirmed', color: 'success' },
  processing: { label: 'In Progress', color: 'info' },
  shipped: { label: 'Shipped', color: 'success' },
  delivered: { label: 'Delivered', color: 'success' },
  cancelled: { label: 'Cancelled', color: 'error' },
  refunded: { label: 'Refunded', color: 'default' },
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

// Public order-status page linked from the confirmation email:
// /orders/<order_lookup_token>. The token is a secret capability; the
// get_order_by_token RPC returns customer-safe fields only.
function OrderStatus() {
  const { token } = useParams();
  const navigate = useNavigate();
  const validToken = UUID_RE.test(token || '');

  const { data: order, isLoading, isError } = useQuery({
    queryKey: ['order-status', token],
    enabled: validToken,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_order_by_token', {
        lookup_token: token,
      });
      if (error) throw error;
      return data; // null when no order matches the token
    },
  });

  if (validToken && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!validToken || isError || !order) {
    return (
      <Box sx={{ textAlign: 'center', minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 2, p: 4 }}>
        <Typography variant="h5">Order not found</Typography>
        <Typography variant="body1" color="text.secondary">
          We couldn&apos;t find an order for this link. Double-check the link from your
          confirmation email, or contact us at info@kbrosleather.com.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/')}>
          Back to Home
        </Button>
      </Box>
    );
  }

  const status = STATUS_LABELS[order.status] || { label: order.status, color: 'default' };
  const placedOn = order.created_at
    ? new Date(order.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Your Order
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            {placedOn && (
              <Typography variant="body2" color="text.secondary">
                Placed on {placedOn}
              </Typography>
            )}
            {order.shipping_name && (
              <Typography variant="body2" color="text.secondary">
                Ships to {order.shipping_name}
                {order.shipping_city ? ` — ${order.shipping_city}, ${order.shipping_state}` : ''}
              </Typography>
            )}
          </Box>
          <Chip label={status.label} color={status.color} />
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(order.items || []).map((item, i) => (
              <TableRow key={i}>
                <TableCell>
                  {item.name}
                  {item.personalization_text && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Personalization: {item.personalization_text}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{item.quantity}</TableCell>
                <TableCell align="right">{money(item.price)}</TableCell>
                <TableCell align="right">{money(item.item_total)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
          <Typography variant="body2">Subtotal: {money(order.subtotal)}</Typography>
          {Number(order.discount_amount) > 0 && (
            <Typography variant="body2">
              Discount{order.discount_code ? ` (${order.discount_code})` : ''}: -
              {money(order.discount_amount)}
            </Typography>
          )}
          {/* shipping_method.price is the pre-discount rate; the saving is
              already inside discount_amount, so showing the discounted rate
              here would subtract it twice. */}
          <Typography variant="body2">
            Shipping: {money(order.shipping_method?.price)}
            {order.shipping_method?.discounted ? ' (free with your code)' : ''}
          </Typography>
          <Typography variant="body2">Tax: {money(order.tax)}</Typography>
          <Typography variant="h6">Total: {money(order.total)}</Typography>
        </Box>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
        Each piece is handmade to order. Questions? Email{' '}
        <a href="mailto:info@kbrosleather.com">info@kbrosleather.com</a>.
      </Typography>
    </Box>
  );
}

export default OrderStatus;
